import { IconButton, Typography, useTheme } from "@mui/material";
import { Trash2 } from "lucide-react";
import {
	type Control,
	type FieldValues,
	useFieldArray,
	useWatch,
} from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { ToolPreviousValue } from "web_myplann/Portfolio/PortfolioShared/ManualValue/toolPreviousValueSchema";
import { ListRow } from "web_myplann/Shared/Components/ListRow";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { numberFormatter } from "web_myplann/Shared/Utils/numberFormatters";

type Props = {
	control: Control<FieldValues>;
};

const PageResources = Resources.Portfolio.AddTools;

export const ToolPreviousValues = ({ control }: Props) => {
	const { t } = useResource();
	const theme = useTheme();
	const previousValues = useFieldArray({ control, name: "previousValues" });
	const values: ToolPreviousValue[] =
		useWatch({ control, name: "previousValues" }) ?? [];

	if (previousValues.fields.length === 0) {
		return null;
	}

	return (
		<>
			<Typography
				variant="bodyStrong"
				px={theme.spacingTokens.lg}
				pt={theme.spacingTokens.md}
				pb={theme.spacingTokens.sm}
			>
				{t(PageResources.previousValues)}
			</Typography>
			{previousValues.fields.map((field, index) => {
				const previousValue = values[index];
				if (!previousValue) {
					return null;
				}
				const value = `${numberFormatter.format(previousValue.value)} ${t(Resources.Common.CurrencySign)}`;
				const date = dateFormatter(previousValue.date);

				return (
					<ListRow
						key={field.id}
						title={value}
						rightText={date}
						isLast={index === previousValues.fields.length - 1}
						rightSlot={
							<IconButton
								variant="ghost"
								aria-label={t(PageResources.deleteValue, { value, date })}
								onClick={() => previousValues.remove(index)}
							>
								<Trash2
									size={theme.iconSize.sm}
									color={theme.palette.grey[400]}
								/>
							</IconButton>
						}
					/>
				);
			})}
		</>
	);
};
